/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faCog, 
  faChartLine, 
  faShieldAlt, 
  faDatabase, 
  faBell, 
  faEye, 
  faEdit, 
  faTrash, 
  faPlus,
  faSearch,
  faFilter,
  faDownload,
  faUpload,
  faBan,
  faCheckCircle,
  faTimesCircle,
  faUserCheck,
  faUserTimes,
  faCrown,
  faUserTie,
  faUserGraduate,
  faBuilding,
  faFileAlt,
  faGlobe,
  faServer,
  faMemory,
  faHdd,
  faNetworkWired,
  faExclamationTriangle,
  faInfoCircle,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import { FadeInUp } from '../components/animations';
import { buildApiUrl, makeAuthenticatedRequest } from '../config/api';
import '../styles/AdminControlPanel.css';

const AdminControlPanel = ({ initialTab = 'overview' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // System Overview Data
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    systemHealth: 'healthy',
    serverLoad: 0,
    memoryUsage: 0,
    diskUsage: 0
  });

  // User Management Data
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({
    jobSeekers: 0,
    recruiters: 0,
    admins: 0,
    newUsersToday: 0,
    activeUsersToday: 0
  });

  // Feature Control Data
  const [featureFlags, setFeatureFlags] = useState({
    jobSearch: true,
    resumeUpload: true,
    profileTracking: true,
    networking: true,
    analytics: true,
    chatBot: true,
    emailNotifications: true,
    smsNotifications: true,
    maintenanceMode: false
  });

  // System Monitoring Data
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // About Platform CMS
  const [aboutHtml, setAboutHtml] = useState('');
  const [aboutSections, setAboutSections] = useState({});
  const [aboutLoading, setAboutLoading] = useState(false);
  const [aboutMessage, setAboutMessage] = useState('');
  const [aboutUpdatedAt, setAboutUpdatedAt] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  const [activeRoleKey, setActiveRoleKey] = useState('job-seeker');
  const [activeFeatureCategory, setActiveFeatureCategory] = useState('ai-ml');
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);

  // Jobs management (super admin view)
  const [adminJobs, setAdminJobs] = useState([]);
  const [adminJobsLoading, setAdminJobsLoading] = useState(false);
  const [adminJobsError, setAdminJobsError] = useState('');
  const [adminJobSearch, setAdminJobSearch] = useState('');

  useEffect(() => {
    // Only load admin data if we're not just showing the About Platform page
    if (initialTab !== 'about') {
      loadAdminData();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'about') {
      loadAboutContent();
    }
  }, [activeTab]);

  // Lazy-load jobs when the Jobs tab is opened
  useEffect(() => {
    if (activeTab === 'jobs' && !adminJobsLoading && adminJobs.length === 0) {
      loadAdminJobs();
    }
  }, [activeTab, adminJobs.length, adminJobsLoading]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Load system overview
      const systemResponse = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/system-stats')
      );
      if (systemResponse?.ok) {
        const systemData = await systemResponse.json();
        setSystemStats(systemData);
      }

      // Load user management data
      const usersResponse = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/users')
      );
      if (usersResponse?.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
        setUserStats(usersData.stats || {});
      }

      // Load feature flags
      const featuresResponse = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/feature-flags')
      );
      if (featuresResponse?.ok) {
        const featuresData = await featuresResponse.json();
        setFeatureFlags(featuresData);
      }

      // Load system alerts
      const alertsResponse = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/alerts')
      );
      if (alertsResponse?.ok) {
        const alertsData = await alertsResponse.json();
        setSystemAlerts(alertsData);
      }

      // Load recent activity
      const activityResponse = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/recent-activity')
      );
      if (activityResponse?.ok) {
        const activityData = await activityResponse.json();
        setRecentActivity(activityData);
      }

    } catch (error) {
      console.error('Error loading admin data:', error);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminJobs = async () => {
    setAdminJobsLoading(true);
    setAdminJobsError('');
    try {
      const res = await makeAuthenticatedRequest(
        buildApiUrl('/api/jobs/get_jobs?limit=200')
      );
      if (res?.ok) {
        const data = await res.json();
        const jobsArray = Array.isArray(data) ? data : [];
        setAdminJobs(jobsArray);
      } else {
        setAdminJobs([]);
        setAdminJobsError('Failed to load jobs');
      }
    } catch (e) {
      console.error('Error loading admin jobs:', e);
      setAdminJobs([]);
      setAdminJobsError('Failed to load jobs');
    } finally {
      setAdminJobsLoading(false);
    }
  };

  const handleAdminToggleJobStatus = async (job) => {
    if (!job || !job._id) return;
    try {
      const res = await makeAuthenticatedRequest(
        buildApiUrl(`/api/jobs/jobs/${job._id}/toggle-status`),
        {
          method: 'PUT'
        }
      );
      if (res?.ok) {
        const data = await res.json().catch(() => ({}));
        const newStatus = (data.status || '').toUpperCase() || 'UPDATED';
        setError(null);
        alert(`Job is now ${newStatus}`);
        // Reload jobs so the list stays accurate
        loadAdminJobs();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to update job status');
      }
    } catch (e) {
      console.error('Error toggling job status as admin:', e);
      alert('Failed to update job status');
    }
  };

  const loadAboutContent = async () => {
    setAboutLoading(true);
    setAboutMessage('');
    try {
      const res = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/pages/about-platform')
      );
      if (res?.ok) {
        const data = await res.json();
        setAboutHtml(data.contentHtml || '');
        
        // Merge saved sections with defaults to ensure all fields are available
        const savedSections = data.sections || {};
        const mergedSections = {};
        
        Object.keys(defaultSections).forEach(sectionId => {
          if (sectionId === 'roles' && savedSections.roles) {
            mergedSections.roles = {
              ...defaultSections.roles,
              ...savedSections.roles,
              roleList: {
                ...defaultSections.roles.roleList,
                ...(savedSections.roles.roleList || {})
              }
            };
            const roleKeys = Object.keys(mergedSections.roles.roleList || {});
            if (roleKeys.length > 0) {
              setActiveRoleKey(roleKeys[0]);
            }
          } else if (sectionId === 'features' && savedSections.features) {
            mergedSections.features = {
              ...defaultSections.features,
              ...savedSections.features,
              categories: {
                ...defaultSections.features.categories,
                ...(savedSections.features.categories || {})
              }
            };
            const catKeys = Object.keys(mergedSections.features.categories || {});
            if (catKeys.length > 0) {
              setActiveFeatureCategory(catKeys[0]);
              const firstCat = mergedSections.features.categories[catKeys[0]];
              if (firstCat?.features?.length) {
                setActiveFeatureIndex(0);
              }
            }
          } else {
            mergedSections[sectionId] = {
              ...defaultSections[sectionId],
              ...(savedSections[sectionId] || {})
            };
          }
        });
        
        setAboutSections(mergedSections);
        setAboutUpdatedAt(data.updatedAt || null);
      } else {
        setAboutMessage('Failed to load About Platform content');
      }
    } catch (e) {
      console.error('Failed to load about content', e);
      setAboutMessage('Failed to load About Platform content');
    } finally {
      setAboutLoading(false);
    }
  };

  const saveAboutContent = async () => {
    setAboutLoading(true);
    setAboutMessage('');
    try {
      const res = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/pages/about-platform'),
        {
          method: 'PUT',
          body: JSON.stringify({ 
            contentHtml: aboutHtml,
            sections: aboutSections
          })
        }
      );
      if (res?.ok) {
        setAboutMessage('Saved successfully');
        await loadAboutContent();
      } else {
        setAboutMessage('Save failed');
      }
    } catch (e) {
      console.error('Failed to save about content', e);
      setAboutMessage('Save failed');
    } finally {
      setAboutLoading(false);
    }
  };

  const saveSection = async (sectionId, sectionData) => {
    setAboutLoading(true);
    setAboutMessage('');
    try {
      const res = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/pages/about-platform/section'),
        {
          method: 'PUT',
          body: JSON.stringify({ 
            sectionId,
            sectionData
          })
        }
      );
      if (res?.ok) {
        setAboutSections(prev => ({ ...prev, [sectionId]: sectionData }));
        setAboutMessage(`Section '${sectionId}' saved successfully`);
        setTimeout(() => setAboutMessage(''), 3000);
      } else {
        setAboutMessage('Save failed');
      }
    } catch (e) {
      console.error('Failed to save section', e);
      setAboutMessage('Save failed');
    } finally {
      setAboutLoading(false);
    }
  };

  const updateSectionField = (sectionId, field, value) => {
    setAboutSections(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [field]: value
      }
    }));
  };

  const handleFeatureToggle = async (feature, enabled) => {
    try {
      const response = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/feature-flags'),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ feature, enabled })
        }
      );

      if (response?.ok) {
        setFeatureFlags(prev => ({ ...prev, [feature]: enabled }));
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      const response = await makeAuthenticatedRequest(
        buildApiUrl(`/api/admin/users/${userId}/${action}`),
        { method: 'POST' }
      );

      if (response?.ok) {
        loadAdminData(); // Refresh data
      }
    } catch (error) {
      console.error('Error performing user action:', error);
    }
  };

  const getSystemHealthColor = (health) => {
    switch (health) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSystemHealthIcon = (health) => {
    switch (health) {
      case 'healthy': return faCheckCircle;
      case 'warning': return faExclamationTriangle;
      case 'critical': return faTimesCircle;
      default: return faInfoCircle;
    }
  };

  // Default section structure
  const defaultSections = {
    hero: {
      title: 'Free AI-Powered Job Network',
      badge: 'FREE WORLDWIDE',
      description: 'Revolutionizing recruitment with advanced AI, multilingual support, and cultural intelligence designed specifically for the global market. Find the perfect match in minutes, not months.',
      stat1Value: '95%',
      stat1Label: 'AI Accuracy',
      stat2Value: '3x',
      stat2Label: 'Faster',
      ctaText: 'Start Your Journey Now'
    },
    roles: {
      title: 'Roles & Benefits',
      subtitle: 'How different users get value from AksharJobs',
      roleList: {
        'job-seeker': {
          title: 'Job Seekers',
          subtitle: 'AI-powered matching and career growth',
          benefits: [
            'Smart matching to relevant jobs based on skills and experience',
            'Multilingual resume support (Swahili, English)',
            'Real-time alerts for new opportunities',
            'Interview prep and profile improvement tips',
            'Mobile-first experience on all devices'
          ]
        },
        'employer': {
          title: 'Employers',
          subtitle: 'Hire top talent and grow your business',
          benefits: [
            'Access to a diverse pool of qualified candidates',
            'AI-powered candidate matching and recommendations',
            'Streamlined recruitment and hiring process',
            'Company branding and visibility tools',
            'Analytics and insights on recruitment performance'
          ]
        },
        'intern': {
          title: 'Interns',
          subtitle: 'Explore careers and build experience',
          benefits: [
            'Best-fit internship matching',
            'Mentor connection and guidance',
            'Portfolio and certificate support',
            'Job placement assistance'
          ]
        },
        'recruiter': {
          title: 'Recruiters',
          subtitle: 'Find top talent faster with AI',
          benefits: [
            '75.3% accurate candidate-job matching',
            'Powerful filters: skills, experience, location',
            'AI resume parsing and insights',
            'Interview scheduling and smart notifications',
            'Analytics on hiring funnel performance'
          ]
        },
        'mentor': {
          title: 'Mentors',
          subtitle: 'Guide and inspire talent',
          benefits: [
            'Connect with mentees aligned to your expertise',
            'Structured goals and progress tracking',
            'Resource library and community support',
            'Recognition for mentorship impact'
          ]
        },
        'trainer': {
          title: 'Trainers',
          subtitle: 'Share knowledge and build skills',
          benefits: [
            'Create and publish training programs',
            'Manage learners and track progress',
            'Issue certificates and credentials',
            'Analytics on learning outcomes and engagement',
            'Marketplace visibility to reach students'
          ]
        },
        'consultant': {
          title: 'Consultants',
          subtitle: 'Provide strategic guidance and expertise',
          benefits: [
            'Connect with clients seeking specialized expertise',
            'Project management and collaboration tools',
            'Secure communication and file sharing',
            'Performance tracking and client feedback',
            'Professional network and referral system'
          ]
        },
        'volunteer': {
          title: 'Volunteers',
          subtitle: 'Make a positive impact in your community',
          benefits: [
            'Connect with meaningful volunteer opportunities',
            'Track volunteer hours and impact',
            'Build skills through hands-on experience',
            'Network with like-minded professionals',
            'Receive recognition and certificates',
            'Access to exclusive volunteer events'
          ]
        },
        'community': {
          title: 'Communities',
          subtitle: 'Network, learn and collaborate',
          benefits: [
            'Professional networking and forums',
            'Events, resources and knowledge sharing',
            'Collaboration and project opportunities',
            'Global and Kenya-based communities'
          ]
        },
        'university': {
          title: 'Universities',
          subtitle: 'Connect students with opportunities',
          benefits: [
            'Student placement and career services',
            'Industry partnership opportunities',
            'Alumni network integration',
            'Research collaboration platforms',
            'Graduate employment tracking'
          ]
        },
        'ngo': {
          title: 'NGOs',
          subtitle: 'Drive social impact and create meaningful change',
          benefits: [
            'Connect with passionate volunteers and professionals',
            'Post volunteer opportunities and project-based roles',
            'Access to skilled talent for social impact initiatives',
            'Partnership opportunities with other organizations',
            'Track volunteer engagement and impact metrics',
            'Build a network of supporters and advocates'
          ]
        }
      }
    },
    features: {
      title: 'Complete Feature Overview',
      subtitle: 'Discover all the powerful features that make AksharJobs the most advanced AI-powered job matching platform',
      categories: {
        'ai-ml': {
          title: '🤖 AI & Machine Learning',
          features: [
            {
              title: 'Fine-Tuned SBERT Model',
              description: '75.3% accuracy in job-resume matching, outperforming industry standards by 15%',
              stats: ['75.3%', '0.0068 MSE'],
              highlight: 'Industry Leading'
            },
            {
              title: 'Intelligent Resume Parsing',
              description: 'Advanced AI extracts structured data from PDF/DOCX resumes using Google Gemini API',
              tags: ['Skills', 'Education', 'Experience']
            },
            {
              title: 'Semantic Similarity Matching',
              description: 'Goes beyond keyword matching to understand context and meaning in job descriptions',
              highlight: 'Context-Aware'
            },
            {
              title: 'AI Improvement Suggestions',
              description: 'Personalized recommendations to help job seekers improve their profiles and applications',
              highlight: 'Personalized'
            }
          ]
        },
        'multilingual': {
          title: '🌍 Multilingual & Cultural Intelligence',
          features: [
            {
              title: 'Multilingual Resume Processing',
              description: 'Process resumes in Swahili, Sheng, local English, and other Kenyan languages',
              stats: ['70% More Talent'],
              tags: ['Swahili', 'Sheng', 'Local English'],
              highlight: 'Unique Feature'
            },
            {
              title: 'Cultural Intelligence',
              description: 'Understand global business cultures, community dynamics, and local market preferences worldwide',
              tags: ['Community Service', 'Local Business', 'Cultural Fit']
            },
            {
              title: 'Local Market Expertise',
              description: 'Analyze formal vs informal sector experience and local business networks',
              highlight: 'Local Focus'
            },
            {
              title: 'Community Integration',
              description: 'Score candidates based on local network connections and community involvement',
              highlight: 'Community-Focused'
            }
          ]
        },
        'analytics': {
          title: '📊 Analytics & Insights',
          features: [
            {
              title: 'Advanced Analytics Dashboard',
              description: 'Comprehensive visualizations including match distribution, application status tracking, and diversity analytics',
              tags: ['Match Distribution', 'Status Tracking', 'Diversity Analytics'],
              highlight: 'Data-Driven'
            },
            {
              title: 'Match Score Visualization',
              description: 'Real-time calculation and visual representation of job-resume compatibility',
              stats: ['80%+ Excellent', '60-79% Good'],
              highlight: 'Real-Time'
            },
            {
              title: 'Performance Metrics',
              description: 'Track hiring success rates, time-to-hire, and candidate satisfaction',
              highlight: 'Performance Tracking'
            },
            {
              title: 'Export & Reporting',
              description: 'Generate detailed reports and export data for further analysis',
              highlight: 'Export Ready'
            }
          ]
        },
        'ux': {
          title: '👥 User Experience & Interface',
          features: [
            {
              title: 'Role-Based Dashboards',
              description: 'Separate, optimized interfaces for job seekers and recruiters',
              listItems: ['Job Seeker Dashboard', 'Recruiter Dashboard', 'Customized workflows', 'Role-specific features']
            },
            {
              title: 'Advanced Job Search',
              description: 'Powerful filtering and search capabilities',
              listItems: ['Location-based filtering', 'Job type filtering', 'Experience level filtering', 'Skills-based search', 'Salary range filtering']
            },
            {
              title: 'Cross-Device Compatibility',
              description: 'Fully responsive design that works perfectly on all devices and screen sizes',
              listItems: ['Mobile phones (iOS & Android)', 'Tablets (iPad, Android tablets)', 'Desktop computers (Windows, Mac)', 'Laptops (all screen sizes)', 'Smart TVs and large displays'],
              highlight: 'Universal Access'
            },
            {
              title: 'Real-Time Updates',
              description: 'Instant notifications and real-time status updates',
              highlight: 'Live Updates'
            },
            {
              title: 'Interview Scheduling',
              description: 'Built-in calendar system for scheduling interviews',
              listItems: ['Calendar integration', 'Automated reminders', 'Time zone support', 'Rescheduling options']
            },
            {
              title: 'Smart Notifications',
              description: 'Intelligent notification system for relevant updates',
              highlight: 'Smart Alerts'
            }
          ]
        }
      }
    },
    about: {
      title: 'Revolutionizing Recruitment Worldwide',
      description: 'AksharJobs is not just another job portal. We\'re a comprehensive AI-powered platform that breaks down language barriers, understands cultural nuances, and connects the right talent with the right opportunities across the globe.',
      feature1: 'Access to 70% more talent through multilingual support',
      feature2: 'Cultural intelligence for better candidate-company fit',
      feature3: 'Advanced AI with industry-leading 75.3% accuracy',
      feature4: 'Comprehensive analytics for data-driven decisions',
      stat1Value: '5+',
      stat1Label: 'Countries',
      stat2Value: '3+',
      stat2Label: 'Languages',
      stat3Value: '100+',
      stat3Label: 'Job Matches',
      stat4Value: '85%',
      stat4Label: 'Success Rate'
    },
    contact: {
      title: 'Get In Touch',
      subtitle: 'Ready to revolutionize your hiring process? Let\'s talk!',
      email: 'info@aksharjobs.com',
      phone: '+254 743 599050',
      address: 'PRJ8+MRR, Swaminarayan Road, Laxcon Court and Plaza , 2nd floor , Nairobi',
      whatsapp: '+254 743 599050'
    },
    stickyCta: {
      text: '🚀 Join 100+ professionals already on AksharJobs! Limited early access spots available.',
      button1Text: 'Get In Touch',
      button2Text: 'Get Early Access'
    }
  };

  const sectionTitles = {
    hero: 'Hero Section',
    roles: 'Roles Section',
    features: 'Features Section',
    about: 'About Section',
    contact: 'Contact Section',
    stickyCta: 'Sticky CTA Bar'
  };

  const getSectionData = (sectionId) => {
    const saved = aboutSections[sectionId] || {};
    const defaults = defaultSections[sectionId] || {};
    
    // Deep merge for nested structures
    if (sectionId === 'roles' && saved.roleList) {
      return {
        ...defaults,
        ...saved,
        roleList: {
          ...defaults.roleList,
          ...saved.roleList
        }
      };
    }
    
    if (sectionId === 'features' && saved.categories) {
      return {
        ...defaults,
        ...saved,
        categories: {
          ...defaults.categories,
          ...saved.categories
        }
      };
    }
    
    return { ...defaults, ...saved };
  };

  const aboutContent = (
    <FadeInUp>
      <div className="security-section">
        <h2>About Platform CMS - Section Editor</h2>
        <p>Edit the About Platform page content section by section. Changes are saved individually.</p>

        <div style={{ margin: '12px 0 20px 0' }}>
          <h3 style={{ marginBottom: '8px' }}>Live Page Preview</h3>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
            <iframe
              title="About Platform Preview"
              src={`${window.location.origin}/about-platform`}
              style={{ width: '100%', height: '520px', border: 'none' }}
            />
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
            This shows the current About Platform page. Save updates below, then refresh this preview to see changes.
          </p>
        </div>

        {/* Section Selector - Tabs Style */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '16px' }}>Select Section to Edit:</label>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px',
            borderBottom: '2px solid #e5e7eb',
            paddingBottom: '12px',
            marginBottom: '20px'
          }}>
            {Object.keys(sectionTitles).map(sectionId => (
              <button
                key={sectionId}
                onClick={() => setActiveSection(sectionId)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: activeSection === sectionId ? '#3b82f6' : '#f3f4f6',
                  color: activeSection === sectionId ? 'white' : '#374151',
                  borderBottom: activeSection === sectionId ? '2px solid #3b82f6' : '2px solid transparent'
                }}
              >
                {sectionTitles[sectionId]}
              </button>
            ))}
          </div>
        </div>

        {/* Section Editors */}
        {activeSection === 'hero' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>Hero Section</h3>
            <div className="form-group">
              <label>Main Title</label>
              <input
                type="text"
                value={getSectionData('hero').title || ''}
                onChange={(e) => updateSectionField('hero', 'title', e.target.value)}
                placeholder="Free AI-Powered Job Network"
              />
            </div>
            <div className="form-group">
              <label>Badge Text</label>
              <input
                type="text"
                value={getSectionData('hero').badge || ''}
                onChange={(e) => updateSectionField('hero', 'badge', e.target.value)}
                placeholder="FREE WORLDWIDE"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={4}
                value={getSectionData('hero').description || ''}
                onChange={(e) => updateSectionField('hero', 'description', e.target.value)}
                placeholder="Revolutionizing recruitment with advanced AI..."
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Stat 1 Value</label>
                <input
                  type="text"
                  value={getSectionData('hero').stat1Value || ''}
                  onChange={(e) => updateSectionField('hero', 'stat1Value', e.target.value)}
                  placeholder="95%"
                />
              </div>
              <div className="form-group">
                <label>Stat 1 Label</label>
                <input
                  type="text"
                  value={getSectionData('hero').stat1Label || ''}
                  onChange={(e) => updateSectionField('hero', 'stat1Label', e.target.value)}
                  placeholder="AI Accuracy"
                />
              </div>
              <div className="form-group">
                <label>Stat 2 Value</label>
                <input
                  type="text"
                  value={getSectionData('hero').stat2Value || ''}
                  onChange={(e) => updateSectionField('hero', 'stat2Value', e.target.value)}
                  placeholder="3x"
                />
              </div>
              <div className="form-group">
                <label>Stat 2 Label</label>
                <input
                  type="text"
                  value={getSectionData('hero').stat2Label || ''}
                  onChange={(e) => updateSectionField('hero', 'stat2Label', e.target.value)}
                  placeholder="Faster"
                />
              </div>
            </div>
            <div className="form-group">
              <label>CTA Button Text</label>
              <input
                type="text"
                value={getSectionData('hero').ctaText || ''}
                onChange={(e) => updateSectionField('hero', 'ctaText', e.target.value)}
                placeholder="Start Your Journey Now"
              />
            </div>
            <button
              className="security-btn"
              onClick={() => saveSection('hero', getSectionData('hero'))}
              disabled={aboutLoading}
              style={{ marginTop: '12px' }}
            >
              {aboutLoading ? 'Saving...' : 'Save Hero Section'}
            </button>
          </div>
        )}

        {activeSection === 'roles' && (() => {
          const rolesData = getSectionData('roles');
          const roleList = rolesData.roleList || defaultSections.roles.roleList;
          const roleKeys = Object.keys(roleList);
          const currentRoleKey = roleKeys.includes(activeRoleKey) ? activeRoleKey : roleKeys[0];
          const role = roleList[currentRoleKey] || {};

          return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '16px' }}>Roles Section</h3>
              
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  value={rolesData.title || ''}
                  onChange={(e) => updateSectionField('roles', 'title', e.target.value)}
                  placeholder="Roles & Benefits"
                />
              </div>
              <div className="form-group">
                <label>Section Subtitle</label>
                <input
                  type="text"
                  value={rolesData.subtitle || ''}
                  onChange={(e) => updateSectionField('roles', 'subtitle', e.target.value)}
                  placeholder="How different users get value from AksharJobs"
                />
              </div>

              <div style={{ marginTop: '24px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Select Role</h4>
                <select
                  value={currentRoleKey}
                  onChange={(e) => setActiveRoleKey(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}
                >
                  {roleKeys.map((roleKey) => (
                    <option key={roleKey} value={roleKey}>{roleList[roleKey]?.title || roleKey}</option>
                  ))}
                </select>

                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px', 
                  padding: '16px', 
                  background: '#f9fafb'
                }}>
                  <h5 style={{ marginBottom: '12px', color: '#1f2937' }}>{role.title || currentRoleKey}</h5>
                  
                  <div className="form-group">
                    <label>Role Title</label>
                    <input
                      type="text"
                      value={role.title || ''}
                      onChange={(e) => {
                        const updatedRoleList = { ...roleList };
                        updatedRoleList[currentRoleKey] = { ...role, title: e.target.value };
                        updateSectionField('roles', 'roleList', updatedRoleList);
                      }}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Role Subtitle</label>
                    <input
                      type="text"
                      value={role.subtitle || ''}
                      onChange={(e) => {
                        const updatedRoleList = { ...roleList };
                        updatedRoleList[currentRoleKey] = { ...role, subtitle: e.target.value };
                        updateSectionField('roles', 'roleList', updatedRoleList);
                      }}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Benefits (one per line)</label>
                    <textarea
                      rows={Math.max(4, (role.benefits?.length || 0) + 1)}
                      value={(role.benefits || []).join('\n')}
                      onChange={(e) => {
                        const benefits = e.target.value.split('\n').filter(b => b.trim());
                        const updatedRoleList = { ...roleList };
                        updatedRoleList[currentRoleKey] = { ...role, benefits };
                        updateSectionField('roles', 'roleList', updatedRoleList);
                      }}
                      placeholder="Enter each benefit on a new line"
                    />
                    <small style={{ color: '#6b7280', fontSize: '12px' }}>
                      {role.benefits?.length || 0} benefit{role.benefits?.length !== 1 ? 's' : ''}
                    </small>
                  </div>
                </div>
              </div>
              
              <button
                className="security-btn"
                onClick={() => saveSection('roles', getSectionData('roles'))}
                disabled={aboutLoading}
                style={{ marginTop: '12px' }}
              >
                {aboutLoading ? 'Saving...' : 'Save Roles Section'}
              </button>
            </div>
          );
        })()}

        {activeSection === 'features' && (() => {
          const featuresData = getSectionData('features');
          const categories = featuresData.categories || defaultSections.features.categories;
          const categoryKeys = Object.keys(categories);
          const currentCategoryKey = categoryKeys.includes(activeFeatureCategory) ? activeFeatureCategory : categoryKeys[0];
          const currentCategory = categories[currentCategoryKey] || {};
          const featureList = currentCategory.features || [];
          const currentFeatureIndex = featureList[activeFeatureIndex] ? activeFeatureIndex : 0;
          const feature = featureList[currentFeatureIndex] || {};
          
          return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '16px' }}>Features Section</h3>
              
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  value={featuresData.title || ''}
                  onChange={(e) => updateSectionField('features', 'title', e.target.value)}
                  placeholder="Complete Feature Overview"
                />
              </div>
              <div className="form-group">
                <label>Section Subtitle</label>
                <textarea
                  rows={3}
                  value={featuresData.subtitle || ''}
                  onChange={(e) => updateSectionField('features', 'subtitle', e.target.value)}
                  placeholder="Discover all the powerful features..."
                />
              </div>

              <div style={{ marginTop: '24px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Select Category</h4>
                <select
                  value={currentCategoryKey}
                  onChange={(e) => {
                    setActiveFeatureCategory(e.target.value);
                    setActiveFeatureIndex(0);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                >
                  {categoryKeys.map((catKey) => (
                    <option key={catKey} value={catKey}>{categories[catKey]?.title || catKey}</option>
                  ))}
                </select>

                <div className="form-group">
                  <label>Category Title</label>
                  <input
                    type="text"
                    value={currentCategory.title || ''}
                    onChange={(e) => {
                      const updatedCategories = { ...categories };
                      updatedCategories[currentCategoryKey] = { ...currentCategory, title: e.target.value };
                      updateSectionField('features', 'categories', updatedCategories);
                    }}
                  />
                </div>

                <h4 style={{ marginBottom: '12px', marginTop: '16px' }}>Select Feature</h4>
                <select
                  value={currentFeatureIndex}
                  onChange={(e) => setActiveFeatureIndex(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                >
                  {featureList.map((f, idx) => (
                    <option key={idx} value={idx}>{f?.title || `Feature ${idx + 1}`}</option>
                  ))}
                </select>

                <div style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '12px',
                  background: 'white'
                }}>
                  <div className="form-group">
                    <label>Feature Title</label>
                    <input
                      type="text"
                      value={feature.title || ''}
                      onChange={(e) => {
                        const updatedFeatures = [...featureList];
                        updatedFeatures[currentFeatureIndex] = { ...feature, title: e.target.value };
                        const updatedCategories = { ...categories };
                        updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                        updateSectionField('features', 'categories', updatedCategories);
                      }}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      rows={2}
                      value={feature.description || ''}
                      onChange={(e) => {
                        const updatedFeatures = [...featureList];
                        updatedFeatures[currentFeatureIndex] = { ...feature, description: e.target.value };
                        const updatedCategories = { ...categories };
                        updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                        updateSectionField('features', 'categories', updatedCategories);
                      }}
                    />
                  </div>
                  
                  {feature.stats && (
                    <div className="form-group">
                      <label>Stats (comma-separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(feature.stats) ? feature.stats.join(', ') : feature.stats}
                        onChange={(e) => {
                          const stats = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                          const updatedFeatures = [...featureList];
                          updatedFeatures[currentFeatureIndex] = { ...feature, stats };
                          const updatedCategories = { ...categories };
                          updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                          updateSectionField('features', 'categories', updatedCategories);
                        }}
                      />
                    </div>
                  )}
                  
                  {feature.tags && (
                    <div className="form-group">
                      <label>Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(feature.tags) ? feature.tags.join(', ') : feature.tags}
                        onChange={(e) => {
                          const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                          const updatedFeatures = [...featureList];
                          updatedFeatures[currentFeatureIndex] = { ...feature, tags };
                          const updatedCategories = { ...categories };
                          updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                          updateSectionField('features', 'categories', updatedCategories);
                        }}
                      />
                    </div>
                  )}
                  
                  {feature.listItems && (
                    <div className="form-group">
                      <label>List Items (one per line)</label>
                      <textarea
                        rows={Math.max(3, (feature.listItems?.length || 0) + 1)}
                        value={Array.isArray(feature.listItems) ? feature.listItems.join('\n') : ''}
                        onChange={(e) => {
                          const listItems = e.target.value.split('\n').filter(item => item.trim());
                          const updatedFeatures = [...featureList];
                          updatedFeatures[currentFeatureIndex] = { ...feature, listItems };
                          const updatedCategories = { ...categories };
                          updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                          updateSectionField('features', 'categories', updatedCategories);
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Highlight Text (optional)</label>
                    <input
                      type="text"
                      value={feature.highlight || ''}
                      onChange={(e) => {
                        const updatedFeatures = [...featureList];
                        updatedFeatures[currentFeatureIndex] = { ...feature, highlight: e.target.value };
                        const updatedCategories = { ...categories };
                        updatedCategories[currentCategoryKey] = { ...currentCategory, features: updatedFeatures };
                        updateSectionField('features', 'categories', updatedCategories);
                      }}
                      placeholder="e.g., Industry Leading, Real-Time"
                    />
                  </div>
                </div>
              </div>
              
              <button
                className="security-btn"
                onClick={() => saveSection('features', getSectionData('features'))}
                disabled={aboutLoading}
                style={{ marginTop: '12px' }}
              >
                {aboutLoading ? 'Saving...' : 'Save Features Section'}
              </button>
            </div>
          );
        })()}

        {activeSection === 'about' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>About Section</h3>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={getSectionData('about').title || ''}
                onChange={(e) => updateSectionField('about', 'title', e.target.value)}
                placeholder="Revolutionizing Recruitment Worldwide"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={4}
                value={getSectionData('about').description || ''}
                onChange={(e) => updateSectionField('about', 'description', e.target.value)}
                placeholder="AksharJobs is not just another job portal..."
              />
            </div>
            <div className="form-group">
              <label>Feature 1</label>
              <input
                type="text"
                value={getSectionData('about').feature1 || ''}
                onChange={(e) => updateSectionField('about', 'feature1', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Feature 2</label>
              <input
                type="text"
                value={getSectionData('about').feature2 || ''}
                onChange={(e) => updateSectionField('about', 'feature2', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Feature 3</label>
              <input
                type="text"
                value={getSectionData('about').feature3 || ''}
                onChange={(e) => updateSectionField('about', 'feature3', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Feature 4</label>
              <input
                type="text"
                value={getSectionData('about').feature4 || ''}
                onChange={(e) => updateSectionField('about', 'feature4', e.target.value)}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div className="form-group">
                <label>Stat 1 Value</label>
                <input
                  type="text"
                  value={getSectionData('about').stat1Value || ''}
                  onChange={(e) => updateSectionField('about', 'stat1Value', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 1 Label</label>
                <input
                  type="text"
                  value={getSectionData('about').stat1Label || ''}
                  onChange={(e) => updateSectionField('about', 'stat1Label', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 2 Value</label>
                <input
                  type="text"
                  value={getSectionData('about').stat2Value || ''}
                  onChange={(e) => updateSectionField('about', 'stat2Value', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 2 Label</label>
                <input
                  type="text"
                  value={getSectionData('about').stat2Label || ''}
                  onChange={(e) => updateSectionField('about', 'stat2Label', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 3 Value</label>
                <input
                  type="text"
                  value={getSectionData('about').stat3Value || ''}
                  onChange={(e) => updateSectionField('about', 'stat3Value', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 3 Label</label>
                <input
                  type="text"
                  value={getSectionData('about').stat3Label || ''}
                  onChange={(e) => updateSectionField('about', 'stat3Label', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 4 Value</label>
                <input
                  type="text"
                  value={getSectionData('about').stat4Value || ''}
                  onChange={(e) => updateSectionField('about', 'stat4Value', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Stat 4 Label</label>
                <input
                  type="text"
                  value={getSectionData('about').stat4Label || ''}
                  onChange={(e) => updateSectionField('about', 'stat4Label', e.target.value)}
                />
              </div>
            </div>
            <button
              className="security-btn"
              onClick={() => saveSection('about', getSectionData('about'))}
              disabled={aboutLoading}
              style={{ marginTop: '12px' }}
            >
              {aboutLoading ? 'Saving...' : 'Save About Section'}
            </button>
          </div>
        )}

        {activeSection === 'contact' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>Contact Section</h3>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={getSectionData('contact').title || ''}
                onChange={(e) => updateSectionField('contact', 'title', e.target.value)}
                placeholder="Get In Touch"
              />
            </div>
            <div className="form-group">
              <label>Subtitle</label>
              <input
                type="text"
                value={getSectionData('contact').subtitle || ''}
                onChange={(e) => updateSectionField('contact', 'subtitle', e.target.value)}
                placeholder="Ready to revolutionize your hiring process? Let's talk!"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={getSectionData('contact').email || ''}
                onChange={(e) => updateSectionField('contact', 'email', e.target.value)}
                placeholder="info@aksharjobs.com"
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="text"
                value={getSectionData('contact').phone || ''}
                onChange={(e) => updateSectionField('contact', 'phone', e.target.value)}
                placeholder="+254 743 599050"
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={getSectionData('contact').address || ''}
                onChange={(e) => updateSectionField('contact', 'address', e.target.value)}
                placeholder="PRJ8+MRR, Swaminarayan Road, Laxcon Court and Plaza , 2nd floor , Nairobi"
              />
            </div>
            <div className="form-group">
              <label>WhatsApp</label>
              <input
                type="text"
                value={getSectionData('contact').whatsapp || ''}
                onChange={(e) => updateSectionField('contact', 'whatsapp', e.target.value)}
                placeholder="+254 743 599050"
              />
            </div>
            <button
              className="security-btn"
              onClick={() => saveSection('contact', getSectionData('contact'))}
              disabled={aboutLoading}
              style={{ marginTop: '12px' }}
            >
              {aboutLoading ? 'Saving...' : 'Save Contact Section'}
            </button>
          </div>
        )}

        {activeSection === 'stickyCta' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>Sticky CTA Bar</h3>
            <div className="form-group">
              <label>CTA Text</label>
              <textarea
                rows={2}
                value={getSectionData('stickyCta').text || ''}
                onChange={(e) => updateSectionField('stickyCta', 'text', e.target.value)}
                placeholder="🚀 Join 100+ professionals already on AksharJobs! Limited early access spots available."
              />
            </div>
            <div className="form-group">
              <label>Button 1 Text</label>
              <input
                type="text"
                value={getSectionData('stickyCta').button1Text || ''}
                onChange={(e) => updateSectionField('stickyCta', 'button1Text', e.target.value)}
                placeholder="Get In Touch"
              />
            </div>
            <div className="form-group">
              <label>Button 2 Text</label>
              <input
                type="text"
                value={getSectionData('stickyCta').button2Text || ''}
                onChange={(e) => updateSectionField('stickyCta', 'button2Text', e.target.value)}
                placeholder="Get Early Access"
              />
            </div>
            <button
              className="security-btn"
              onClick={() => saveSection('stickyCta', getSectionData('stickyCta'))}
              disabled={aboutLoading}
              style={{ marginTop: '12px' }}
            >
              {aboutLoading ? 'Saving...' : 'Save Sticky CTA'}
            </button>
          </div>
        )}

        {/* HTML Editor (Legacy) */}
        <div style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
          <h3>Custom HTML Editor (Legacy)</h3>
          <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
            Use this only if you want to replace the entire page with custom HTML. Section editing above is recommended.
          </p>
          <div className="form-group">
            <label>HTML Content</label>
            <textarea
              rows={10}
              value={aboutHtml}
              onChange={(e) => setAboutHtml(e.target.value)}
              placeholder="Paste or type custom HTML for the About Platform page"
            />
          </div>
          <button
            className="security-btn"
            onClick={saveAboutContent}
            disabled={aboutLoading}
            style={{ marginTop: '12px' }}
          >
            {aboutLoading ? 'Saving...' : 'Save HTML Content'}
          </button>
        </div>

        {aboutUpdatedAt && (
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '12px' }}>
            Last updated: {aboutUpdatedAt}
          </p>
        )}
        {aboutMessage && (
          <p style={{ 
            color: aboutMessage.includes('fail') ? '#dc2626' : '#10b981',
            marginTop: '12px',
            padding: '8px',
            background: aboutMessage.includes('fail') ? '#fee2e2' : '#d1fae5',
            borderRadius: '4px'
          }}>
            {aboutMessage}
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button
            className="security-btn"
            onClick={loadAboutContent}
            disabled={aboutLoading}
          >
            Refresh All
          </button>
        </div>
      </div>
    </FadeInUp>
  );

  // Only show loading for admin panel if we're not just showing About Platform
  const isAboutOnly = initialTab === 'about' && activeTab === 'about';
  
  if (loading && !isAboutOnly) {
    return (
      <div className="admin-control-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (isAboutOnly) {
    return (
      <div className="admin-control-panel" style={{ padding: 0 }}>
        <div
          className="admin-content"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            padding: 0
          }}
        >
          <main
            className="admin-main"
            style={{
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              padding: '0 16px 24px 16px',
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            <div style={{ width: '100%', maxWidth: '1200px' }}>
              {aboutContent}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-control-panel">
      <div className="admin-header">
        <h1>Admin Control Panel</h1>
        <div className="admin-actions">
          <button className="refresh-btn" onClick={loadAdminData}>
            <FontAwesomeIcon icon={faSearch} />
            Refresh Data
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faChartLine} />
          System Overview
        </button>
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FontAwesomeIcon icon={faUsers} />
          User Management
        </button>
        <button 
          className={`tab ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          <FontAwesomeIcon icon={faCog} />
          Feature Control
        </button>
        <button 
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          <FontAwesomeIcon icon={faBriefcase} />
          Deactivate Jobs
        </button>
        <button 
          className={`tab ${activeTab === 'monitoring' ? 'active' : ''}`}
          onClick={() => setActiveTab('monitoring')}
        >
          <FontAwesomeIcon icon={faEye} />
          System Monitoring
        </button>
        <button 
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          Security
        </button>
        <button 
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          <FontAwesomeIcon icon={faFileAlt} />
          About Platform CMS
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'overview' && (
          <FadeInUp>
            <div className="overview-section">
              <h2>System Overview</h2>
              
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div className="stat-content">
                    <h3>{systemStats.totalUsers.toLocaleString()}</h3>
                    <p>Total Users</p>
                    <span className="stat-change positive">+{userStats.newUsersToday} today</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="stat-content">
                    <h3>{systemStats.totalJobs.toLocaleString()}</h3>
                    <p>Total Jobs</p>
                    <span className="stat-change positive">{systemStats.activeJobs} active</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faFileAlt} />
                  </div>
                  <div className="stat-content">
                    <h3>{systemStats.totalApplications.toLocaleString()}</h3>
                    <p>Applications</p>
                    <span className="stat-change positive">+12% this week</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ color: getSystemHealthColor(systemStats.systemHealth) }}>
                    <FontAwesomeIcon icon={getSystemHealthIcon(systemStats.systemHealth)} />
                  </div>
                  <div className="stat-content">
                    <h3 style={{ color: getSystemHealthColor(systemStats.systemHealth) }}>
                      {systemStats.systemHealth.charAt(0).toUpperCase() + systemStats.systemHealth.slice(1)}
                    </h3>
                    <p>System Health</p>
                    <span className="stat-change">Server Load: {systemStats.serverLoad}%</span>
                  </div>
                </div>
              </div>

              <div className="system-metrics">
                <div className="metric-card">
                  <h4>Server Performance</h4>
                  <div className="metric-item">
                    <span>CPU Usage</span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${systemStats.serverLoad}%` }}
                      ></div>
                    </div>
                    <span>{systemStats.serverLoad}%</span>
                  </div>
                  <div className="metric-item">
                    <span>Memory Usage</span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${systemStats.memoryUsage}%` }}
                      ></div>
                    </div>
                    <span>{systemStats.memoryUsage}%</span>
                  </div>
                  <div className="metric-item">
                    <span>Disk Usage</span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${systemStats.diskUsage}%` }}
                      ></div>
                    </div>
                    <span>{systemStats.diskUsage}%</span>
                  </div>
                </div>

                <div className="metric-card">
                  <h4>User Distribution</h4>
                  <div className="user-distribution">
                    <div className="user-type">
                      <FontAwesomeIcon icon={faUserGraduate} />
                      <span>Job Seekers</span>
                      <span className="count">{userStats.jobSeekers}</span>
                    </div>
                    <div className="user-type">
                      <FontAwesomeIcon icon={faUserTie} />
                      <span>Recruiters</span>
                      <span className="count">{userStats.recruiters}</span>
                    </div>
                    <div className="user-type">
                      <FontAwesomeIcon icon={faCrown} />
                      <span>Admins</span>
                      <span className="count">{userStats.admins}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>
        )}

        {activeTab === 'users' && (
          <FadeInUp>
            <div className="users-section">
              <h2>User Management</h2>
              
              <div className="users-header">
                <div className="search-box">
                  <FontAwesomeIcon icon={faSearch} />
                  <input type="text" placeholder="Search users..." />
                </div>
                <div className="filter-controls">
                  <select>
                    <option value="all">All Users</option>
                    <option value="jobSeekers">Job Seekers</option>
                    <option value="recruiters">Recruiters</option>
                    <option value="admins">Admins</option>
                  </select>
                </div>
              </div>

              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} />
                              ) : (
                                <div className="avatar-placeholder">
                                  {user.name?.charAt(0)}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="user-name">{user.name}</div>
                              <div className="user-email">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${user.status}`}>
                            {user.status}
                          </span>
                        </td>
                        <td>{user.lastActive}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-btn view"
                              onClick={() => navigate(`/profile/${user.id}`)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button 
                              className="action-btn edit"
                              onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            {user.status === 'active' ? (
                              <button 
                                className="action-btn ban"
                                onClick={() => handleUserAction(user.id, 'suspend')}
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </button>
                            ) : (
                              <button 
                                className="action-btn activate"
                                onClick={() => handleUserAction(user.id, 'activate')}
                              >
                                <FontAwesomeIcon icon={faUserCheck} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeInUp>
        )}

        {activeTab === 'features' && (
          <FadeInUp>
            <div className="features-section">
              <h2>Feature Control</h2>
              <p>Enable or disable features across the platform</p>
              
              <div className="feature-flags">
                {Object.entries(featureFlags).map(([feature, enabled]) => (
                  <div key={feature} className="feature-flag">
                    <div className="feature-info">
                      <h4>{feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
                      <p>Control the {feature} feature availability</p>
                    </div>
                    <div className="feature-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => handleFeatureToggle(feature, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeInUp>
        )}

        {activeTab === 'jobs' && (
          <FadeInUp>
            <div className="monitoring-section">
              <h2>Deactivate Jobs</h2>
              <p>View recent jobs across the platform and deactivate old or incorrect listings. Deactivated jobs disappear from the homepage and jobseeker Browse Jobs.</p>

              <div style={{ margin: '16px 0', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div className="search-box" style={{ maxWidth: '320px', flex: '1 1 200px' }}>
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder="Search by title, company, or location..."
                    value={adminJobSearch}
                    onChange={(e) => setAdminJobSearch(e.target.value)}
                  />
                </div>
                <button
                  className="refresh-btn"
                  onClick={loadAdminJobs}
                  disabled={adminJobsLoading}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {adminJobsLoading ? 'Refreshing...' : 'Reload Jobs'}
                </button>
              </div>

              {adminJobsError && (
                <div className="alerts-section">
                  <div className="alert-item critical">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <div className="alert-content">
                      <h4>Error loading jobs</h4>
                      <p>{adminJobsError}</p>
                    </div>
                  </div>
                </div>
              )}

              {adminJobsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading jobs...</p>
                </div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Company</th>
                        <th>Location</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const search = (adminJobSearch || '').trim().toLowerCase();
                        const filtered = adminJobs.filter((job) => {
                          if (!search) return true;
                          const title = (job.job_title || job.jobTitle || job.title || '').toLowerCase();
                          const company = (job.company_name || job.companyName || job.company || '').toLowerCase();
                          const location = (job.location || '').toLowerCase();
                          return (
                            title.includes(search) ||
                            company.includes(search) ||
                            location.includes(search)
                          );
                        });

                        if (!filtered.length) {
                          return (
                            <tr>
                              <td colSpan={7}>
                                <div className="no-alerts">
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                  <p>No jobs match your current filters.</p>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return filtered.map((job) => {
                          const status = (job.status || 'active').toLowerCase();
                          const created =
                            job.created_at || job.createdAt || job.postedDate || job.postedAt;
                          const createdDisplay = created ? new Date(created).toLocaleDateString() : 'N/A';

                          return (
                            <tr key={job._id || job.id}>
                              <td>
                                <div className="user-info">
                                  <div className="user-avatar">
                                    {(job.job_title || job.title || 'J').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="user-name">
                                      {job.job_title || job.jobTitle || job.title || 'Untitled Job'}
                                    </div>
                                    <div className="user-email" style={{ fontSize: '12px' }}>
                                      {(job.description || '').slice(0, 80) ||
                                        'No description provided.'}
                                      {(job.description || '').length > 80 && '...'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="role-badge">
                                  {job.company_name || job.companyName || job.company || 'N/A'}
                                </span>
                              </td>
                              <td>{job.location || 'N/A'}</td>
                              <td>{job.job_type || job.jobType || job.type || 'N/A'}</td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    status === 'active' ? 'active' : 'inactive'
                                  }`}
                                >
                                  {status.toUpperCase()}
                                </span>
                              </td>
                              <td>{createdDisplay}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="action-btn edit"
                                    onClick={() => window.open(`/job-details/${job._id}`, '_blank')}
                                    title="View full job details"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </button>
                                  <button
                                    className="action-btn ban"
                                    onClick={() => handleAdminToggleJobStatus(job)}
                                    title={
                                      status === 'active'
                                        ? 'Deactivate this job'
                                        : 'Reactivate this job'
                                    }
                                  >
                                    <FontAwesomeIcon icon={faBan} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </FadeInUp>
        )}

        {activeTab === 'monitoring' && (
          <FadeInUp>
            <div className="monitoring-section">
              <h2>System Monitoring</h2>
              
              <div className="alerts-section">
                <h3>System Alerts</h3>
                {systemAlerts.length > 0 ? (
                  <div className="alerts-list">
                    {systemAlerts.map((alert, index) => (
                      <div key={index} className={`alert-item ${alert.severity}`}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <div className="alert-content">
                          <h4>{alert.title}</h4>
                          <p>{alert.message}</p>
                          <span className="alert-time">{alert.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-alerts">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <p>No active alerts</p>
                  </div>
                )}
              </div>

              <div className="activity-section">
                <h3>Recent Activity</h3>
                <div className="activity-list">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        <FontAwesomeIcon icon={faUserCheck} />
                      </div>
                      <div className="activity-content">
                        <p>{activity.description}</p>
                        <span className="activity-time">{activity.timestamp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeInUp>
        )}

        {activeTab === 'security' && (
          <FadeInUp>
            <div className="security-section">
              <h2>Security & Access Control</h2>
              
              <div className="security-cards">
                <div className="security-card">
                  <h3>Access Control</h3>
                  <div className="security-item">
                    <span>Admin Access</span>
                    <button className="security-btn">Configure</button>
                  </div>
                  <div className="security-item">
                    <span>Role Permissions</span>
                    <button className="security-btn">Manage</button>
                  </div>
                  <div className="security-item">
                    <span>API Access</span>
                    <button className="security-btn">Settings</button>
                  </div>
                </div>

                <div className="security-card">
                  <h3>Security Monitoring</h3>
                  <div className="security-item">
                    <span>Failed Login Attempts</span>
                    <span className="security-value">12</span>
                  </div>
                  <div className="security-item">
                    <span>Blocked IPs</span>
                    <span className="security-value">3</span>
                  </div>
                  <div className="security-item">
                    <span>Security Score</span>
                    <span className="security-value good">95%</span>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>
        )}

        {activeTab === 'about' && aboutContent}
      </div>
    </div>
  );
};

export default AdminControlPanel;
